// IntakeFormPage — /intake/:token. PUBLIC, tenant-facing pre-lease intake form.
//
// An applicant (a tenant, NO login) lands here from the link broker's 'intake'
// 302. The page talks ONLY to the public, token-gated /api/intake/:token
// endpoints via lib/intakeClient.ts (no Bearer token). It renders the WHP design
// system but, unlike the rest of the SPA, is NOT wrapped in the authenticated
// AppShell — it's a standalone branded surface (see App.tsx).
//
// SCOPING (driven entirely by the server's view, never recomputed client-side):
//   • a PRIMARY applicant sees every visible section + a delegation control +
//     suspected-duplicate prompts; they edit the combined animal roster.
//   • a DELEGATE sees only their assigned animals and/or the designation.
//
// The form mirrors the server's IntakeFormView shape. On submit it POSTs only
// the sections it was allowed to edit; the server re-gates and applies the
// authoredBy merge. A clean "submitted, thank you" state renders when the view
// comes back state:'submitted'.

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getIntake,
  submitIntake,
  IntakeApiError,
  type IntakeFormView,
} from '@/lib/intakeClient';
import { isLikelySelfDesignation } from '@/lib/designeeSelfMatch';
import { ANIMAL_QUESTIONS_VERSION } from '@/lib/animalQuestions';
import {
  intakeAnimalReasons,
  intakeAnimalsToSend,
  intakeAnimalToDraft,
  newIntakeAnimalDraft,
  type IntakeAnimalDraft,
} from '@/lib/intakeAnimalForm';
import { AnimalQuestionFields } from '@/components/AnimalQuestionFields';

// ---------------------------------------------------------------------------
// Editable local state (a working copy of the server's view fields)
// ---------------------------------------------------------------------------

// One roster row: the shared per-animal answers (lib/animalDraft.ts, the same
// shape the Animal Addendum form edits) plus the intake's own flags. The pure
// helpers live in lib/intakeAnimalForm.ts (WEL-173) so they can be tested.
type AnimalDraft = IntakeAnimalDraft;

interface DesignationDraft {
  designeeName: string;
  relationship: string;
  phone: string;
  email: string;
  addressLine1: string;
  addressLine2: string;
  city: string;
  state: string;
  zip: string;
}

// ---------------------------------------------------------------------------
// Brand header (matches the link-broker page lockup, DS tokens)
// ---------------------------------------------------------------------------

function BrandLockup({ propertyAddress }: { propertyAddress: string | null }): React.JSX.Element {
  return (
    <header style={{ textAlign: 'center', marginBottom: 'var(--space-7)' }}>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 26,
          lineHeight: 1.2,
          letterSpacing: '0.16em',
          textTransform: 'uppercase',
          color: 'var(--fg)',
        }}
      >
        Welcome&nbsp;Home
      </div>
      <div
        style={{
          fontFamily: 'var(--font-serif)',
          fontSize: 13,
          lineHeight: 1.2,
          letterSpacing: '0.42em',
          textTransform: 'uppercase',
          color: 'var(--accent)',
          paddingLeft: '0.42em',
        }}
      >
        Properties
      </div>
      <div
        style={{
          width: 56,
          height: 1,
          backgroundColor: 'var(--accent)',
          margin: 'var(--space-4) auto 0',
        }}
      />
      {propertyAddress ? (
        <p
          style={{
            marginTop: 'var(--space-4)',
            fontFamily: 'var(--font-sans)',
            fontSize: 'var(--text-md)',
            color: 'var(--fg-muted)',
          }}
        >
          {propertyAddress}
        </p>
      ) : null}
    </header>
  );
}

function SectionCard(props: {
  title: string;
  description?: string;
  children: React.ReactNode;
}): React.JSX.Element {
  return (
    <section className="card" style={{ marginBottom: 'var(--space-5)' }}>
      <div style={{ marginBottom: 'var(--space-5)' }}>
        <h2
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 'var(--text-lg)',
            fontWeight: 400,
            color: 'var(--fg)',
            margin: 0,
          }}
        >
          {props.title}
        </h2>
        {props.description ? (
          <p className="modal-description" style={{ marginTop: 'var(--space-2)' }}>
            {props.description}
          </p>
        ) : null}
      </div>
      {props.children}
    </section>
  );
}

/**
 * WEL-46: a single section whose request staff cancelled. Calm, read-only —
 * reuses SectionCard so it sits in the flow exactly like a live section, but
 * shows a friendly "no action needed" note instead of inputs.
 */
function CancelledSectionCard({ title }: { title: string }): React.JSX.Element {
  return (
    <SectionCard title={title}>
      <div className="alert" data-tone="info">
        <div className="alert-body">
          <div className="alert-title">This request has been canceled</div>
          No action is needed for this section.
        </div>
      </div>
    </SectionCard>
  );
}

// ---------------------------------------------------------------------------
// Page
// ---------------------------------------------------------------------------

export function IntakeFormPage(): React.JSX.Element {
  const { token = '' } = useParams<{ token: string }>();

  const [view, setView] = useState<IntakeFormView | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [preparingLease, setPreparingLease] = useState(false);
  const [leaseHandoffTimedOut, setLeaseHandoffTimedOut] = useState(false);

  // Editable working copies (seeded from the view on load / after submit).
  const [animals, setAnimals] = useState<AnimalDraft[]>([]);
  // WEL-54: an explicit "do you have any animals?" answer. Without it, an
  // applicant with no animals on file could submit an empty list, which records
  // the section as completed-with-zero — indistinguishable from "never
  // answered". Seeded Yes when the roster already has an animal; otherwise null
  // (unanswered) so the form must collect a real answer before submit.
  const [hasAnimals, setHasAnimals] = useState<boolean | null>(null);
  const [designation, setDesignation] = useState<DesignationDraft>({
    designeeName: '',
    relationship: '',
    phone: '',
    email: '',
    addressLine1: '',
    addressLine2: '',
    city: '',
    state: '',
    zip: '',
  });
  const [yardPref, setYardPref] = useState<'resident_maintains' | 'lessor_arranges' | ''>('');

  // Delegation control (primary): map of delegateId -> { animalIds, designation }.
  const [delegateTarget, setDelegateTarget] = useState<string>('');
  const [delegateAnimalIds, setDelegateAnimalIds] = useState<Set<string>>(new Set());
  const [delegateDesignation, setDelegateDesignation] = useState(false);

  const seedFromView = useCallback((v: IntakeFormView) => {
    setView(v);
    const animalList = v.sections.animals.list;
    setAnimals(animalList.map(intakeAnimalToDraft));
    // Seed the have-animals answer: a non-empty roster ⇒ Yes; an empty list on
    // an already-completed section ⇒ the applicant previously declared None;
    // otherwise leave it unanswered so we collect an explicit answer.
    setHasAnimals(
      animalList.length > 0
        ? true
        : v.sections.animals.status === 'completed'
          ? false
          : null,
    );
    const d = v.sections.designation.data;
    setDesignation({
      designeeName: d.designeeName ?? '',
      relationship: d.relationship ?? '',
      phone: d.phone ?? '',
      email: d.email ?? '',
      addressLine1: d.addressLine1 ?? '',
      addressLine2: d.addressLine2 ?? '',
      city: d.city ?? '',
      state: d.state ?? '',
      zip: d.zip ?? '',
    });
    setYardPref(v.sections.yardCare.data.arrangement ?? '');
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setLoadError(null);
    setPreparingLease(false);
    setLeaseHandoffTimedOut(false);
    getIntake(token)
      .then((v) => {
        if (alive) seedFromView(v);
      })
      .catch((err) => {
        if (!alive) return;
        setLoadError(
          err instanceof IntakeApiError
            ? 'We could not load this form. The link may have expired.'
            : 'Something went wrong loading this form.',
        );
      })
      .finally(() => {
        if (alive) setLoading(false);
      });
    return () => {
      alive = false;
    };
  }, [token, seedFromView]);

  // A final intake submit fires the renderer asynchronously, so the public POST
  // never waits on PDF/DocuSeal/SES. Poll the already-token-gated view instead;
  // `ready` is the server's per-applicant authorization to enter /sign/:uuid.
  useEffect(() => {
    if (!preparingLease) return;
    let alive = true;
    let timer: number | undefined;
    const startedAt = Date.now();

    const poll = async () => {
      try {
        const next = await getIntake(token);
        if (!alive) return;
        seedFromView(next);
        const handoff = next.leaseHandoff;
        if (handoff?.state === 'ready' && handoff.signUrl) {
          window.location.assign(handoff.signUrl);
          return;
        }
        // A non-signer has reached their honest terminal state: the lead's
        // signing invite is the next action, not a link that would dead-end.
        if (handoff?.state === 'emailed' || handoff?.state === 'none') {
          setPreparingLease(false);
          return;
        }
      } catch {
        // Keep trying through the short grace window. The completion card below
        // gives a calm, actionable outcome if the render path is delayed.
      }

      if (!alive) return;
      if (Date.now() - startedAt >= 60_000) {
        setPreparingLease(false);
        setLeaseHandoffTimedOut(true);
        return;
      }
      timer = window.setTimeout(poll, 2_000);
    };

    timer = window.setTimeout(poll, 2_000);
    return () => {
      alive = false;
      if (timer !== undefined) window.clearTimeout(timer);
    };
  }, [preparingLease, seedFromView, token]);

  const updateAnimal = useCallback((id: string, patch: Partial<AnimalDraft>) => {
    setAnimals((prev) => prev.map((a) => (a.id === id ? { ...a, ...patch } : a)));
  }, []);

  const handleSubmit = useCallback(async () => {
    if (!view) return;
    // The designee's name is the one thing this section exists to collect: the
    // lease cannot be generated without it, so a blank submission only moves the
    // gap onto staff. Everything else about the designee stays optional.
    if (
      view.sections.designation.visible &&
      view.sections.designation.status !== 'cancelled' &&
      !designation.designeeName.trim()
    ) {
      setSaveError('Please name your designee before submitting — the name is the one part we need.');
      return;
    }
    setSaving(true);
    setSaveError(null);
    try {
      // WEL-46: never submit a section staff cancelled, even if still "visible".
      const body: Parameters<typeof submitIntake>[1] = {};
      if (view.sections.animals.visible && view.sections.animals.status !== 'cancelled') {
        // WEL-54: the answer drives what we record. "No" submits a genuinely
        // empty list so the recorded roster is a real declared-none (not an
        // accidental empty, and not ghost removed-rows). "Yes" submits the
        // roster. (The server's primary merge still preserves any animal a
        // delegate or staff authored, so "No" never wipes someone else's entry.)
        body.animals = intakeAnimalsToSend(hasAnimals, animals);
        // WEL-173: built from the question matrix, so the API holds it to the
        // same required answers the form just checked.
        body.questionsVersion = ANIMAL_QUESTIONS_VERSION;
      }
      if (view.sections.designation.visible && view.sections.designation.status !== 'cancelled') {
        body.designation = {
          ...(designation.designeeName.trim() ? { designeeName: designation.designeeName.trim() } : {}),
          ...(designation.relationship.trim() ? { relationship: designation.relationship.trim() } : {}),
          ...(designation.phone.trim() ? { phone: designation.phone.trim() } : {}),
          ...(designation.email.trim() ? { email: designation.email.trim() } : {}),
          ...(designation.addressLine1.trim() ? { addressLine1: designation.addressLine1.trim() } : {}),
          ...(designation.addressLine2.trim() ? { addressLine2: designation.addressLine2.trim() } : {}),
          ...(designation.city.trim() ? { city: designation.city.trim() } : {}),
          ...(designation.state.trim() ? { state: designation.state.trim() } : {}),
          ...(designation.zip.trim() ? { zip: designation.zip.trim() } : {}),
        };
      }
      if (
        view.sections.yardCare.visible &&
        view.sections.yardCare.status !== 'cancelled' &&
        yardPref
      ) {
        body.yardCare = { arrangement: yardPref };
      }
      const res = await submitIntake(token, body);
      seedFromView(res.view);
      setLeaseHandoffTimedOut(false);
      const handoff = res.view.leaseHandoff;
      if (res.view.state === 'submitted' && handoff?.state === 'preparing') {
        setPreparingLease(true);
      } else {
        setPreparingLease(false);
        if (res.view.state === 'submitted' && handoff?.state === 'ready' && handoff.signUrl) {
          window.location.assign(handoff.signUrl);
        }
      }
    } catch (err) {
      setSaveError(
        err instanceof IntakeApiError
          ? ((err.body as { error?: string } | null)?.error ??
              'We could not save your answers. Please try again.')
          : 'We could not save your answers. Please try again.',
      );
    } finally {
      setSaving(false);
    }
  }, [view, animals, hasAnimals, designation, yardPref, token, seedFromView]);

  const handleDelegate = useCallback(async () => {
    if (!view || !delegateTarget) return;
    setSaving(true);
    setSaveError(null);
    try {
      const res = await submitIntake(token, {
        delegations: [
          {
            delegateId: delegateTarget,
            ...(delegateAnimalIds.size ? { animalIds: [...delegateAnimalIds] } : {}),
            ...(delegateDesignation ? { assignDesignation: true } : {}),
          },
        ],
      });
      seedFromView(res.view);
      setDelegateTarget('');
      setDelegateAnimalIds(new Set());
      setDelegateDesignation(false);
    } catch (err) {
      setSaveError(
        err instanceof IntakeApiError
          ? ((err.body as { error?: string } | null)?.error ?? 'Could not assign that person.')
          : 'Could not assign that person.',
      );
    } finally {
      setSaving(false);
    }
  }, [view, delegateTarget, delegateAnimalIds, delegateDesignation, token, seedFromView]);

  const shell = useMemo(
    () =>
      (children: React.ReactNode): React.JSX.Element => (
        <div
          style={{
            minHeight: '100vh',
            backgroundColor: 'var(--bg-alt)',
            padding: 'var(--space-7) var(--space-4)',
          }}
        >
          <div style={{ maxWidth: 720, margin: '0 auto' }}>{children}</div>
        </div>
      ),
    [],
  );

  // --- Loading / error / terminal states ---
  if (loading) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <p style={{ textAlign: 'center', color: 'var(--fg-muted)' }}>Loading…</p>
      </>,
    );
  }
  if (loadError || !view) {
    return shell(
      <>
        <BrandLockup propertyAddress={null} />
        <div className="alert" data-tone="warning">
          <div className="alert-body">
            <div className="alert-title">This link is no longer available</div>
            {loadError ?? 'Please reply to the email we sent you and a real person will help.'}
          </div>
        </div>
      </>,
    );
  }
  if (view.state === 'unavailable') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress} />
        <SectionCard
          title="Nothing to fill out right now"
          description="This link is valid, but there are no details for you to provide at the moment. If we need something from you, we'll send a fresh link."
        >
          <></>
        </SectionCard>
      </>,
    );
  }
  if (view.state === 'sorted') {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress} />
        <SectionCard
          title="Handled by your household's primary applicant"
          description="Your household's resident designation has already been provided by the primary applicant on your application — there's nothing for you to do here. If we need anything else from you, we'll send a fresh link."
        >
          <></>
        </SectionCard>
      </>,
    );
  }
  if (view.state === 'submitted') {
    const handoff = view.leaseHandoff;
    const continueUrl =
      (handoff?.state === 'ready' || handoff?.state === 'emailed')
        ? handoff.signUrl
        : undefined;
    if (preparingLease) {
      return shell(
        <>
          <BrandLockup propertyAddress={view.propertyAddress} />
          <div className="card" data-tone="success" style={{ textAlign: 'center' }}>
            <h1
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 28,
                fontWeight: 400,
                margin: 0,
                color: 'var(--fg)',
              }}
            >
              Preparing your lease…
            </h1>
            <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
              We&apos;re putting your confirmed details into the lease now. We&apos;ll take you
              straight to it as soon as it&apos;s ready.
            </p>
          </div>
        </>,
      );
    }
    if (handoff?.state === 'emailed' && !continueUrl) {
      const lead = handoff.leadFirstName ?? 'the lead applicant';
      return shell(
        <>
          <BrandLockup propertyAddress={view.propertyAddress} />
          <div className="card" data-tone="success" style={{ textAlign: 'center' }}>
            <h1
              style={{
                fontFamily: 'var(--font-serif)',
                fontSize: 28,
                fontWeight: 400,
                margin: 0,
                color: 'var(--fg)',
              }}
            >
              That&apos;s everything.
            </h1>
            <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
              {lead} and any other signers will receive the lease to sign.
            </p>
          </div>
        </>,
      );
    }
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress} />
        <div className="card" data-tone="success" style={{ textAlign: 'center' }}>
          <h1
            style={{
              fontFamily: 'var(--font-serif)',
              fontSize: 28,
              fontWeight: 400,
              margin: 0,
              color: 'var(--fg)',
            }}
          >
            Thank you — everything is in.
          </h1>
          <p style={{ marginTop: 'var(--space-4)', color: 'var(--fg-muted)' }}>
            {leaseHandoffTimedOut
              ? 'We’re preparing your lease and will email your signing link shortly.'
              : <>
                  We&apos;ve received your details for the upcoming lease. There&apos;s nothing more to
                  do right now. When your lease is ready, we&apos;ll email you a link to review and sign
                  it.
                </>}
          </p>
          {continueUrl ? (
            <a
              className="btn"
              data-size="lg"
              href={continueUrl}
              style={{ display: 'inline-flex', marginTop: 'var(--space-5)' }}
            >
              Continue to your lease
            </a>
          ) : null}
        </div>
      </>,
    );
  }

  // --- Fillable form ---
  const { sections } = view;

  // WEL-46: a section is "live" (fillable) only when visible AND not cancelled.
  // If staff cancelled every visible section there is nothing left to do — show
  // a page-level "request canceled" closing state (mirrors 'submitted'/'sorted').
  const visibleSectionStatuses = [
    sections.designation,
    sections.yardCare,
    sections.animals,
  ]
    .filter((s) => s.visible)
    .map((s) => s.status);
  const allCancelled =
    visibleSectionStatuses.length > 0 &&
    visibleSectionStatuses.every((s) => s === 'cancelled');
  if (allCancelled) {
    return shell(
      <>
        <BrandLockup propertyAddress={view.propertyAddress} />
        <SectionCard
          title="This request has been canceled"
          description="There's nothing for you to fill out here — this request was canceled and no action is needed. If we need anything from you, we'll send a fresh link."
        >
          <></>
        </SectionCard>
      </>,
    );
  }

  const greetingName = view.applicant.name ? `, ${view.applicant.name.split(/\s+/)[0]}` : '';

  // WEL-54: when the animals section is live, the applicant must answer the
  // "do you have any animals?" question before submit — so a completion always
  // carries a real Yes/No, never an ambiguous empty list. "Yes" additionally
  // requires at least one animal on file (otherwise it would record the same
  // ambiguous empty list "No" is meant to disambiguate — nudge them instead).
  const animalsLive =
    sections.animals.visible && sections.animals.status !== 'cancelled';
  const hasActiveAnimal = animals.some((a) => !a.removed);
  // WEL-173: each animal this applicant answers for must carry its kind's
  // required answers (a dog's breed, weight and age; a cat's age and color…) —
  // the same rule the API enforces, said in plain words.
  const animalReasons =
    animalsLive && hasAnimals === true
      ? intakeAnimalReasons(animals, { applicantId: view.applicant.id, role: view.role })
      : [];
  const animalsAnswered =
    !animalsLive ||
    hasAnimals === false ||
    (hasAnimals === true && hasActiveAnimal && animalReasons.length === 0);
  const animalsHint =
    animalsLive && hasAnimals === null
      ? 'Please answer whether you have any animals first.'
      : animalsLive && hasAnimals === true && !hasActiveAnimal
        ? 'Add at least one animal, or choose "No animals".'
        : (animalReasons[0] ?? null);

  return shell(
    <>
      <BrandLockup propertyAddress={view.propertyAddress} />
      <div style={{ marginBottom: 'var(--space-6)', textAlign: 'center' }}>
        <h1
          style={{
            fontFamily: 'var(--font-serif)',
            fontSize: 26,
            fontWeight: 400,
            margin: 0,
            color: 'var(--fg)',
          }}
        >
          A few details before your lease{greetingName}
        </h1>
        <p style={{ marginTop: 'var(--space-3)', color: 'var(--fg-muted)' }}>
          Filling these in now means your lease arrives ready to sign — no back-and-forth.
        </p>
      </div>

      {saveError ? (
        <div className="alert" data-tone="danger" style={{ marginBottom: 'var(--space-5)' }}>
          <div className="alert-body">{saveError}</div>
        </div>
      ) : null}

      {sections.animals.visible && sections.animals.status === 'cancelled' ? (
        <CancelledSectionCard title="Your animals" />
      ) : null}

      {sections.animals.visible && sections.animals.status !== 'cancelled' ? (
        <AnimalsSection
          animals={animals}
          suspects={view.suspects}
          hasAnimals={hasAnimals}
          onAnswer={(answer) => {
            setHasAnimals(answer);
            // Switching to "No" clears any in-progress rows so the roster
            // matches the answer; switching to "Yes" with nothing on file opens
            // a blank card so there's something to fill in.
            if (answer === false) {
              setAnimals((prev) => prev.map((a) => ({ ...a, removed: true })));
            } else if (answer === true && animals.every((a) => a.removed)) {
              setAnimals([newIntakeAnimalDraft(`new-${Date.now()}-0`)]);
            }
          }}
          onUpdate={updateAnimal}
          onAdd={() =>
            setAnimals((prev) => [...prev, newIntakeAnimalDraft(`new-${Date.now()}-${prev.length}`)])
          }
        />
      ) : null}

      {sections.designation.visible && sections.designation.status === 'cancelled' ? (
        <CancelledSectionCard title="Household designee" />
      ) : null}

      {sections.designation.visible && sections.designation.status !== 'cancelled' ? (
        <SectionCard
          title="Household designee"
          description="If you were to pass away while living here, this is the person you authorize to enter the home, collect your belongings, and receive any money we owe you. Choose someone who doesn't live with you, and tell us how to reach them."
        >
          <DesignationFields
            value={designation}
            onChange={setDesignation}
            {...(view.applicant.name ? { fillerName: view.applicant.name } : {})}
          />
        </SectionCard>
      ) : null}

      {sections.yardCare.visible && sections.yardCare.status === 'cancelled' ? (
        <CancelledSectionCard title="Yard & landscaping" />
      ) : null}

      {sections.yardCare.visible && sections.yardCare.status !== 'cancelled' ? (
        <SectionCard
          title="Yard &amp; landscaping"
          description="At this home, keeping up the yard and landscaping is the resident's responsibility. Will you take care of it yourselves, or would you like us to arrange yard care for you and bill it to your account?"
        >
          <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap' }}>
            <label className="radio-tile" style={radioTileStyle(yardPref === 'resident_maintains')}>
              <input
                type="radio"
                name="yardCare"
                checked={yardPref === 'resident_maintains'}
                onChange={() => setYardPref('resident_maintains')}
              />
              <span>We&apos;ll take care of the yard ourselves</span>
            </label>
            <label className="radio-tile" style={radioTileStyle(yardPref === 'lessor_arranges')}>
              <input
                type="radio"
                name="yardCare"
                checked={yardPref === 'lessor_arranges'}
                onChange={() => setYardPref('lessor_arranges')}
              />
              <span>Have us arrange it &amp; bill my account</span>
            </label>
          </div>
        </SectionCard>
      ) : null}

      {view.role === 'primary' && view.delegatableApplicants.length > 0 ? (
        <SectionCard
          title="Hand part of this to someone else"
          description="If another applicant should fill out their own animal's details or the household designee, assign it to them and we'll email them their own link."
        >
          <DelegationControl
            applicants={view.delegatableApplicants}
            animals={animals}
            target={delegateTarget}
            onTarget={setDelegateTarget}
            selectedAnimalIds={delegateAnimalIds}
            onToggleAnimal={(id) =>
              setDelegateAnimalIds((prev) => {
                const next = new Set(prev);
                if (next.has(id)) next.delete(id);
                else next.add(id);
                return next;
              })
            }
            assignDesignation={delegateDesignation}
            onToggleDesignation={() => setDelegateDesignation((v) => !v)}
            designationVisible={
              sections.designation.visible && sections.designation.status !== 'cancelled'
            }
            onAssign={handleDelegate}
            disabled={saving}
          />
        </SectionCard>
      ) : null}

      <div
        style={{
          display: 'flex',
          justifyContent: 'flex-end',
          alignItems: 'center',
          gap: 'var(--space-3)',
          marginTop: 'var(--space-6)',
        }}
      >
        {animalsHint ? (
          <span className="helper" style={{ color: 'var(--fg-muted)' }}>
            {animalsHint}
          </span>
        ) : null}
        <button
          className="btn"
          data-size="lg"
          disabled={saving || !animalsAnswered}
          onClick={handleSubmit}
        >
          {saving ? 'Saving…' : 'Submit my details'}
        </button>
      </div>
    </>,
  );
}

// ---------------------------------------------------------------------------
// Animals section
// ---------------------------------------------------------------------------

function AnimalsSection(props: {
  animals: AnimalDraft[];
  suspects: IntakeFormView['suspects'];
  hasAnimals: boolean | null;
  onAnswer: (answer: boolean) => void;
  onUpdate: (id: string, patch: Partial<AnimalDraft>) => void;
  onAdd: () => void;
}): React.JSX.Element {
  const { animals, suspects, hasAnimals, onAnswer, onUpdate, onAdd } = props;
  const active = animals.filter((a) => !a.removed);
  return (
    <SectionCard
      title="Your animals"
      description="Will any animals be living in the home? If so, please confirm and complete the details for each one. If an animal was listed twice, mark it as a duplicate."
    >
      {/* WEL-54: an explicit Yes/No so a completed section always carries a real
          answer — never an ambiguous "completed with no animals" that reads like
          a bug to staff. */}
      <div style={{ display: 'flex', gap: 'var(--space-3)', flexWrap: 'wrap', marginBottom: 'var(--space-4)' }}>
        <label className="radio-tile" style={radioTileStyle(hasAnimals === true)}>
          <input
            type="radio"
            name="hasAnimals"
            checked={hasAnimals === true}
            onChange={() => onAnswer(true)}
          />
          <span>Yes, we have animal{'(s)'}</span>
        </label>
        <label className="radio-tile" style={radioTileStyle(hasAnimals === false)}>
          <input
            type="radio"
            name="hasAnimals"
            checked={hasAnimals === false}
            onChange={() => onAnswer(false)}
          />
          <span>No animals</span>
        </label>
      </div>

      {hasAnimals === false ? (
        <p style={{ color: 'var(--fg-muted)' }}>
          Got it — we&apos;ve recorded that there are no animals for this home.
        </p>
      ) : null}

      {hasAnimals === true ? (
        <>
          {suspects.length > 0 ? (
            <div className="alert" data-tone="info" style={{ marginBottom: 'var(--space-4)' }}>
              <div className="alert-body">
                <div className="alert-title">
                  Possible duplicate{suspects.length > 1 ? 's' : ''}
                </div>
                {suspects.map((s) => (
                  <div key={s.key} style={{ marginTop: 'var(--space-2)' }}>
                    <strong>{s.a.name || 'Unnamed'}</strong> ({s.a.applicant}) and{' '}
                    <strong>{s.b.name || 'Unnamed'}</strong> ({s.b.applicant}) look like they could
                    be the same animal. If so, mark one as a duplicate below.
                  </div>
                ))}
              </div>
            </div>
          ) : null}

          {active.length === 0 ? (
            <p style={{ color: 'var(--fg-muted)' }}>Add each animal that will live in the home.</p>
          ) : (
            active.map((a) => <AnimalCard key={a.id} animal={a} all={active} onUpdate={onUpdate} />)
          )}

          <button
            className="btn"
            data-variant="secondary"
            data-size="sm"
            onClick={onAdd}
            style={{ marginTop: 'var(--space-3)' }}
          >
            + Add an animal
          </button>
        </>
      ) : null}
    </SectionCard>
  );
}

function AnimalCard(props: {
  animal: AnimalDraft;
  all: AnimalDraft[];
  onUpdate: (id: string, patch: Partial<AnimalDraft>) => void;
}): React.JSX.Element {
  const { animal: a, all, onUpdate } = props;
  const others = all.filter((o) => o.id !== a.id);

  return (
    <div
      className="card"
      style={{ marginBottom: 'var(--space-3)', padding: 'var(--space-4)' }}
    >
      {a.sourceApplicantName ? (
        <div className="helper" style={{ marginBottom: 'var(--space-2)' }}>
          Listed by {a.sourceApplicantName}
        </div>
      ) : null}
      {/* WEL-173: the same questions the Animal Addendum form asks — what kind
          of animal, then only that kind's questions. */}
      <AnimalQuestionFields
        idPrefix={`intake-animal-${a.id}`}
        value={a}
        onChange={(patch) => onUpdate(a.id, patch)}
        licenseHelper="If you don't have it yet, leave it blank and please sort it out soon."
      />

      <div
        style={{
          display: 'flex',
          gap: 'var(--space-3)',
          alignItems: 'center',
          marginTop: 'var(--space-3)',
          flexWrap: 'wrap',
        }}
      >
        {others.length > 0 ? (
          <label className="field" style={{ margin: 0 }}>
            <span style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
              Duplicate of
            </span>
            <select
              className="input"
              value={a.duplicateOfId ?? ''}
              onChange={(e) => onUpdate(a.id, { duplicateOfId: e.target.value || null })}
            >
              <option value="">Not a duplicate</option>
              {others.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.name || 'Unnamed animal'}
                </option>
              ))}
            </select>
          </label>
        ) : null}
        <button
          className="btn"
          data-variant="danger-ghost"
          data-size="sm"
          onClick={() => onUpdate(a.id, { removed: true })}
        >
          Remove
        </button>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Designation fields
// ---------------------------------------------------------------------------

function DesignationFields(props: {
  value: DesignationDraft;
  onChange: (v: DesignationDraft) => void;
  /** Who is filling this in — WEL-139 compares the designee against them. */
  fillerName?: string;
}): React.JSX.Element {
  const { value, onChange, fillerName } = props;
  // WEL-139: the designee has to be someone OTHER than the resident. A name
  // match isn't proof (a parent can share a name), so this warns and never
  // blocks — the form still submits.
  const looksLikeSelf = isLikelySelfDesignation(value.designeeName, fillerName);
  return (
    <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 'var(--space-3)' }}>
      <div className="field" style={{ gridColumn: '1 / -1' }}>
        <label htmlFor="designee-name">Designee name</label>
        <input
          id="designee-name"
          className="input"
          value={value.designeeName}
          required
          aria-required="true"
          onChange={(e) => onChange({ ...value, designeeName: e.target.value })}
          {...(looksLikeSelf ? { 'aria-describedby': 'designee-self-warning' } : {})}
        />
        {looksLikeSelf && (
          <div
            id="designee-self-warning"
            className="alert"
            data-tone="warning"
            role="status"
            style={{ marginTop: 'var(--space-2)' }}
          >
            <div className="alert-body">
              That&rsquo;s your own name. Your designee should be someone else &mdash; the person
              who would handle your belongings and any money we owe you if you passed away. If this
              really is a different person who happens to share your name, go ahead and leave it.
            </div>
          </div>
        )}
      </div>
      <div className="field">
        <label>Relationship</label>
        <input
          className="input"
          value={value.relationship}
          onChange={(e) => onChange({ ...value, relationship: e.target.value })}
        />
      </div>
      <div className="field">
        <label>Phone</label>
        <input
          className="input"
          inputMode="tel"
          value={value.phone}
          onChange={(e) => onChange({ ...value, phone: e.target.value })}
        />
      </div>
      <div className="field">
        <label>Email</label>
        <input
          className="input"
          inputMode="email"
          value={value.email}
          onChange={(e) => onChange({ ...value, email: e.target.value })}
        />
      </div>
      <div className="field" style={{ gridColumn: '1 / -1' }}>
        <label>Designee&apos;s mailing address</label>
        <input
          className="input"
          autoComplete="address-line1"
          placeholder="Street address"
          value={value.addressLine1}
          onChange={(e) => onChange({ ...value, addressLine1: e.target.value })}
        />
        <span className="helper">
          Where the designee can be reached by mail (this is their own address, not the rental). Optional.
        </span>
      </div>
      <div className="field" style={{ gridColumn: '1 / -1' }}>
        <label>Apt, suite, etc. (optional)</label>
        <input
          className="input"
          autoComplete="address-line2"
          value={value.addressLine2}
          onChange={(e) => onChange({ ...value, addressLine2: e.target.value })}
        />
      </div>
      <div className="field">
        <label>City</label>
        <input
          className="input"
          autoComplete="address-level2"
          value={value.city}
          onChange={(e) => onChange({ ...value, city: e.target.value })}
        />
      </div>
      <div className="field">
        <label>State</label>
        <input
          className="input"
          autoComplete="address-level1"
          value={value.state}
          onChange={(e) => onChange({ ...value, state: e.target.value })}
        />
      </div>
      <div className="field">
        <label>ZIP</label>
        <input
          className="input"
          inputMode="numeric"
          autoComplete="postal-code"
          value={value.zip}
          onChange={(e) => onChange({ ...value, zip: e.target.value })}
        />
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Delegation control (primary only)
// ---------------------------------------------------------------------------

function DelegationControl(props: {
  applicants: IntakeFormView['delegatableApplicants'];
  animals: AnimalDraft[];
  target: string;
  onTarget: (id: string) => void;
  selectedAnimalIds: Set<string>;
  onToggleAnimal: (id: string) => void;
  assignDesignation: boolean;
  onToggleDesignation: () => void;
  designationVisible: boolean;
  onAssign: () => void;
  disabled: boolean;
}): React.JSX.Element {
  const active = props.animals.filter((a) => !a.removed);
  const canAssign =
    !!props.target && (props.selectedAnimalIds.size > 0 || props.assignDesignation);
  return (
    <div>
      <div className="field">
        <label>Assign to</label>
        <select
          className="input"
          value={props.target}
          onChange={(e) => props.onTarget(e.target.value)}
        >
          <option value="">Choose an applicant…</option>
          {props.applicants.map((a) => (
            <option key={a.id} value={a.id}>
              {a.name}
              {a.email ? ` (${a.email})` : ''}
            </option>
          ))}
        </select>
      </div>

      {active.length > 0 ? (
        <fieldset style={{ border: 0, padding: 0, margin: 'var(--space-3) 0 0' }}>
          <legend style={{ fontSize: 'var(--text-sm)', color: 'var(--fg-muted)' }}>
            Which animals should they fill out?
          </legend>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)' }}>
            {active.map((a) => (
              <label key={a.id} style={{ display: 'flex', gap: 'var(--space-2)' }}>
                <input
                  type="checkbox"
                  checked={props.selectedAnimalIds.has(a.id)}
                  onChange={() => props.onToggleAnimal(a.id)}
                />
                <span>{a.name || 'Unnamed animal'}</span>
              </label>
            ))}
          </div>
        </fieldset>
      ) : null}

      {props.designationVisible ? (
        <label style={{ display: 'flex', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
          <input
            type="checkbox"
            checked={props.assignDesignation}
            onChange={props.onToggleDesignation}
          />
          <span>Let them fill out the household designee</span>
        </label>
      ) : null}

      <button
        className="btn"
        data-variant="secondary"
        onClick={props.onAssign}
        disabled={props.disabled || !canAssign}
        style={{ marginTop: 'var(--space-4)' }}
      >
        Assign &amp; send them a link
      </button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Small style helpers
// ---------------------------------------------------------------------------

function radioTileStyle(selected: boolean): React.CSSProperties {
  return {
    display: 'flex',
    alignItems: 'center',
    gap: 'var(--space-2)',
    padding: 'var(--space-3) var(--space-4)',
    border: `1px solid ${selected ? 'var(--accent)' : 'var(--border)'}`,
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    backgroundColor: selected ? 'var(--accent-soft)' : 'var(--surface)',
  };
}
